'use client';
import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useRouter } from 'next/navigation';
import { Coffee, Dumbbell, BookOpen, Shirt, Package, Wrench, ArrowRight } from 'lucide-react';

const DREAM_ICONS = [Coffee, Dumbbell, BookOpen, Shirt, Package, Wrench];
const DREAM_COLORS = [
  { text: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20', glow: 'hover:shadow-primary/10' },
  { text: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/20', glow: 'hover:shadow-accent/10' },
  { text: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/20', glow: 'hover:shadow-yellow-400/10' },
  { text: 'text-mint', bg: 'bg-mint/10', border: 'border-mint/20', glow: 'hover:shadow-mint/10' },
  { text: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/20', glow: 'hover:shadow-pink-400/10' },
  { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20', glow: 'hover:shadow-orange-400/10' },
];

export default function DreamsWeHelpBuild() {
  const { t } = useLanguage();
  const dw = (t as any)?.dreamsWeHelpBuild;
  const router = useRouter();

  const dreams = [
    {
      title: dw?.d1Title ?? 'Open a Bakery',
      desc: dw?.d1Desc ?? 'From recipes to brand identity, product pricing, and a local launch strategy — your bakery dream fully mapped.',
      cta: dw?.d1Cta ?? 'Analyze This Dream',
      dream: 'I want to open a bakery.',
    },
    {
      title: dw?.d2Title ?? 'Start a Fitness Coaching Business',
      desc: dw?.d2Desc ?? 'Build your coaching brand, define your services, create content, and launch your client acquisition plan.',
      cta: dw?.d2Cta ?? 'Analyze This Dream',
      dream: 'I want to start a fitness coaching business.',
    },
    {
      title: dw?.d3Title ?? 'Publish a Children\'s Book',
      desc: dw?.d3Desc ?? 'From story concept to publishing strategy, brand identity, and marketing plan for your book launch.',
      cta: dw?.d3Cta ?? 'Analyze This Dream',
      dream: 'I want to publish a children\'s book.',
    },
    {
      title: dw?.d4Title ?? 'Launch a Clothing Brand',
      desc: dw?.d4Desc ?? 'Design your brand identity, define your product line, build your content strategy, and plan your launch.',
      cta: dw?.d4Cta ?? 'Analyze This Dream',
      dream: 'I want to launch a clothing brand.',
    },
    {
      title: dw?.d5Title ?? 'Sell Digital Products',
      desc: dw?.d5Desc ?? 'Identify your best digital product ideas, pricing model, platform strategy, and automated sales funnel.',
      cta: dw?.d5Cta ?? 'Analyze This Dream',
      dream: 'I want to sell digital products.',
    },
    {
      title: dw?.d6Title ?? 'Build a Local Service Business',
      desc: dw?.d6Desc ?? 'Define your services, pricing, local marketing strategy, and a 90-day plan to get your first clients.',
      cta: dw?.d6Cta ?? 'Analyze This Dream',
      dream: 'I want to build a local service business.',
    },
  ];

  return (
    <section className="py-20 px-6 lg:px-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/3 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-700 uppercase tracking-widest text-orange-400 mb-3 px-3 py-1 rounded-full bg-orange-400/10 border border-orange-400/20">
            {dw?.sectionLabel ?? 'Example Dreams'}
          </span>
          <h2 className="text-3xl md:text-4xl font-800 text-foreground mb-4">
            {dw?.sectionTitle ?? 'Real Dreams,'}{' '}
            <span className="gradient-text">{dw?.sectionTitleHighlight ?? 'Real Blueprints'}</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            {dw?.sectionSubtitle ?? 'Dream DNA Studio™ works for any dream. Here are six examples of what gets built when you enter your idea.'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {dreams.map((dream, i) => {
            const color = DREAM_COLORS[i];
            const DreamIcon = DREAM_ICONS[i];

            return (
              <div
                key={i}
                className={`glass-card rounded-2xl border border-border p-6 group cursor-pointer transition-all duration-300 hover:border-primary/30 hover:shadow-xl ${color.glow} hover:-translate-y-1`}
                onClick={() => router.push(`/dream-workspace?dream=${encodeURIComponent(dream.dream)}`)}
              >
                <div className={`w-12 h-12 rounded-2xl ${color.bg} border ${color.border} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-200`}>
                  <DreamIcon size={22} className={color.text} />
                </div>

                <h3 className="text-lg font-800 text-foreground mb-2 group-hover:text-primary transition-colors duration-200">
                  {dream.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  {dream.desc}
                </p>

                <div className={`inline-flex items-center gap-1.5 text-xs font-700 ${color.text} group-hover:gap-2.5 transition-all duration-200`}>
                  {dream.cta}
                  <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground mb-4">
            {dw?.bottomText ?? 'Have a different dream? Dream DNA Studio™ works for any idea.'}
          </p>
          <button
            onClick={() => router.push('/dream-workspace')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-700 text-sm hover:bg-primary/90 transition-all duration-200 hover:shadow-lg hover:shadow-primary/20"
          >
            {dw?.bottomCta ?? 'Discover Your Dream DNA™'}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
